import {
  distancePointToSegment,
  ensureCounterClockwise,
  insetPolygon,
  localToWorld,
  pointInOrOnPolygon,
  worldToLocal,
  type Obb2,
  type Vec2,
} from '@indusketch/geometry';
import { buildingCorners, storeyCorners } from '../building.js';
import { PV_LOAD, roofSnowLoad } from '../loads.js';
import {
  hostHall,
  levelElevations,
  levelLabel,
  officeSlabLevels,
  RAILING_HEIGHT,
} from '../office.js';
import { sectionOf } from '../steelProfiles.js';
import { fillText, modelTexts } from '../texts.js';
import type { Building, OfficeSystem, ProjectDocument, ProjectSettings, Wall } from '../types.js';
import { formatNumber } from '../units.js';
import { projectHallFrame, type GridAxis } from './hallStructure.js';

/** Usual column grid of an office skeleton (about 6 × 1.25 m fit-out modules). */
export const DEFAULT_OFFICE_GRID = 7.5;
const CONCRETE_WEIGHT = 25;
/** Screed, ceiling and services on a slab (kN/m²). */
const FINISHES = 1.5;
/** Use of an office floor including partitions, of a storage deck and of a roof kept clear. */
const OFFICE_USE = 3;
const STORAGE_USE = 5;
const MAINTENANCE_USE = 1;
const GAMMA_G = 1.35;
const GAMMA_Q = 1.5;
/** Design stress of a C30/37 column with 1 % reinforcement (0.85 fcd + ρ fyd), kN/m². */
const COLUMN_STRESS = 18_800;
/** Allowance for buckling and unintended eccentricity on the plain compression. */
const SLENDERNESS = 1.3;
const MIN_COLUMN = 0.3;
const MIN_SLAB = 0.18;
/** Span over depth of slabs: one-way on walls, flat slab on columns, between beams. */
const SPAN_RATIO: Readonly<Record<OfficeSystem, number>> = { walls: 32, flat: 30, beams: 35 };
const BEAM_RATIO = 12;
/** Slabs on walls further apart than this want columns or a beam in between. */
const WIDE_SPAN = 10;
const MASSIVE: readonly Wall['cladding'][] = ['sand-lime', 'concrete', 'brick'];

const roundUp = (v: number, step: number) => Math.ceil(v / step - 1e-9) * step;

/** Column of an office skeleton in storey `level`, from its floor up to the slab or beam. */
export interface OfficeColumn {
  readonly center: Vec2;
  readonly size: number;
  readonly level: number;
  readonly base: number;
  readonly top: number;
  readonly outline: readonly Vec2[];
  readonly obb: Obb2;
  /** Characteristic load it carries down (kN), for its footing. */
  readonly load: number;
  /** Stands in the facade. */
  readonly edge: boolean;
}

/** Beam under the slab above storey `level`, between two columns. */
export interface OfficeBeam {
  readonly a: Vec2;
  readonly b: Vec2;
  readonly level: number;
  /** Underside of the slab it carries. */
  readonly top: number;
  readonly width: number;
  /** How far it reaches below the slab. */
  readonly drop: number;
  readonly outline: readonly Vec2[];
}

export interface OfficeStructureInfo {
  readonly system: OfficeSystem;
  readonly grid: number;
  /** Fields of the column grid and their size along the building's width and depth. */
  readonly fields: {
    readonly nx: number;
    readonly ny: number;
    readonly x: number;
    readonly y: number;
  };
  readonly axes: readonly GridAxis[];
  readonly columns: readonly OfficeColumn[];
  readonly beams: readonly OfficeBeam[];
  /** Column cross section per storey (set by hand or sized from the loads). */
  readonly columnSizes: readonly number[];
  /** Total height (with the slab) and width of the beams. */
  readonly beam: { readonly height: number; readonly width: number } | null;
  /** Largest span of the slabs between what carries them. */
  readonly span: number;
  readonly slabRecommended: number;
  readonly hints: readonly string[];
}

type StructureDoc = Pick<ProjectDocument, 'buildings' | 'walls'> & {
  readonly settings?: Pick<ProjectSettings, 'loads'>;
};

const cache = new WeakMap<
  Building,
  { buildings: unknown; walls: unknown; settings: unknown; result: OfficeStructureInfo | null }
>();

/**
 * Pre-sized load-bearing structure of an office: for a skeleton the column grid, the columns
 * per storey (cross section from the floor area each carries and the storeys above), the beams
 * and the slab depth the span needs; for slabs on walls the span between the walls. With the
 * planning hints (slab too thin, columns without support below, clashes with the hall).
 */
export function officeStructure(doc: StructureDoc, b: Building): OfficeStructureInfo | null {
  if (!b.office) return null;
  const hit = cache.get(b);
  if (
    hit &&
    hit.buildings === doc.buildings &&
    hit.walls === doc.walls &&
    hit.settings === doc.settings?.loads
  )
    return hit.result;
  const result = computeOfficeStructure(doc, b);
  cache.set(b, {
    buildings: doc.buildings,
    walls: doc.walls,
    settings: doc.settings?.loads,
    result,
  });
  return result;
}

/** Corners of a square column. */
function square(c: Vec2, size: number, rotation: number): Vec2[] {
  const h = size / 2;
  return [
    { x: -h, y: -h },
    { x: h, y: -h },
    { x: h, y: h },
    { x: -h, y: h },
  ].map((p) => localToWorld(p, { position: c, rotation }));
}

/** Evenly spread lines from `lo` to `hi`, at most `grid` apart. */
function divide(lo: number, hi: number, grid: number): number[] {
  const n = Math.max(1, Math.ceil((hi - lo) / grid - 1e-6));
  return Array.from({ length: n + 1 }, (_, i) => lo + ((hi - lo) * i) / n);
}

/** Concrete stays this far behind the outer face of the facade, so it never shows through. */
export const FACADE_COVER = 0.02;

/**
 * Moves a part centred at `p` (half extents `hx`, `hy` along the building's axes) inwards
 * until it keeps `FACADE_COVER` behind every edge of the counter-clockwise outline it is near.
 */
function pushInside(p: Vec2, hx: number, hy: number, outline: readonly Vec2[]): Vec2 {
  let at = p;
  outline.forEach((a, i) => {
    const c = outline[(i + 1) % outline.length] as Vec2;
    const len = Math.hypot(c.x - a.x, c.y - a.y);
    if (len < 1e-9) return;
    const dir = { x: (c.x - a.x) / len, y: (c.y - a.y) / len };
    const n = { x: -dir.y, y: dir.x };
    const reach = hx * Math.abs(n.x) + hy * Math.abs(n.y) + FACADE_COVER;
    const s = (at.x - a.x) * dir.x + (at.y - a.y) * dir.y;
    const across = hx * Math.abs(dir.x) + hy * Math.abs(dir.y);
    if (s < -across || s > len + across) return;
    const inwards = (at.x - a.x) * n.x + (at.y - a.y) * n.y;
    if (inwards < reach && inwards > -reach)
      at = { x: at.x + n.x * (reach - inwards), y: at.y + n.y * (reach - inwards) };
  });
  return at;
}

const distanceToRing = (p: Vec2, ring: readonly Vec2[]): number =>
  Math.min(
    ...ring.map((a, i) => distancePointToSegment(p, { a, b: ring[(i + 1) % ring.length] as Vec2 })),
  );

function computeOfficeStructure(doc: StructureDoc, b: Building): OfficeStructureInfo | null {
  const o = b.office;
  if (!o) return null;
  const t = modelTexts().officeStructure;
  const cfg = o.structure;
  const system: OfficeSystem = cfg?.system ?? 'walls';
  const grid = cfg?.grid ?? DEFAULT_OFFICE_GRID;
  const tw = b.wallThickness;
  const d = o.slabThickness;
  const e = levelElevations(o);
  const n = o.floors.length;
  const placement = { position: b.center, rotation: b.rotation };
  const local = buildingCorners(b).map((p) => worldToLocal(p, placement));
  const box = {
    x0: Math.min(...local.map((p) => p.x)),
    x1: Math.max(...local.map((p) => p.x)),
    y0: Math.min(...local.map((p) => p.y)),
    y1: Math.max(...local.map((p) => p.y)),
  };
  const toWorld = (u: number, v: number) => localToWorld({ x: u, y: v }, placement);
  const hints: string[] = [];
  const name = b.name;
  const cm = (m: number) => formatNumber(m * 100, 0);
  const m1 = (m: number) => formatNumber(m, 1, 1);

  // Loads of the slab that carries storey `level` (its floor) or the roof above the top storey.
  const slabLoad = (level: number): { g: number; q: number } => {
    const own = CONCRETE_WEIGHT * d + FINISHES;
    if (level < n) return { g: own, q: OFFICE_USE };
    if (o.placement === 'indoor') return { g: own, q: o.usableTop ? STORAGE_USE : MAINTENANCE_USE };
    return { g: own + (b.pv ? PV_LOAD : 0), q: roofSnowLoad(doc.settings?.loads) };
  };

  const xs = divide(box.x0 + tw / 2, box.x1 - tw / 2, grid);
  const ys = divide(box.y0 + tw / 2, box.y1 - tw / 2, grid);
  const gx = (xs[1] ?? box.x1) - (xs[0] ?? box.x0);
  const gy = (ys[1] ?? box.y1) - (ys[0] ?? box.y0);

  let span: number;
  let slabRecommended: number;
  const columns: OfficeColumn[] = [];
  const beams: OfficeBeam[] = [];
  const columnSizes: number[] = [];
  let beam: OfficeStructureInfo['beam'] = null;
  let axes: GridAxis[] = [];

  if (system === 'walls') {
    span = wallSpan(doc, b, box, placement);
    slabRecommended = Math.max(MIN_SLAB, roundUp(span / SPAN_RATIO.walls, 0.01));
    if (span > WIDE_SPAN) hints.push(fillText(t.wideSpan, { name, span: m1(span) }));
  } else {
    // Beams span the wider column distance, the slab the narrower one between the beams.
    const beamsAlongX = gx >= gy;
    span = system === 'flat' ? Math.max(gx, gy) : Math.min(gx, gy);
    slabRecommended = Math.max(
      system === 'flat' ? 0.2 : MIN_SLAB,
      roundUp(span / SPAN_RATIO[system], 0.01),
    );
    if (system === 'beams') {
      const beamSpan = Math.max(gx, gy);
      const height = Math.max(0.4, roundUp(beamSpan / BEAM_RATIO, 0.05));
      beam = { height, width: Math.max(0.3, roundUp(height / 2, 0.05)) };
    }
    const drop = beam ? Math.max(0, beam.height - d) : 0;

    // Positions per storey in local coordinates: grid crossings inside, the facade divided.
    const levels: Vec2[][] = [];
    for (let level = 0; level < n; level++) {
      const outline = ensureCounterClockwise(
        storeyCorners(b, level).map((p) => worldToLocal(p, placement)),
      );
      const centre = insetPolygon(outline, tw / 2);
      const pts: Vec2[] = [];
      const add = (p: Vec2) => {
        if (!pts.some((q) => Math.hypot(q.x - p.x, q.y - p.y) < 0.3)) pts.push(p);
      };
      centre.forEach((a, i) => {
        const c = centre[(i + 1) % centre.length] as Vec2;
        const len = Math.hypot(c.x - a.x, c.y - a.y);
        const k = Math.max(1, Math.ceil(len / grid - 1e-6));
        for (let j = 0; j < k; j++)
          add({ x: a.x + ((c.x - a.x) * j) / k, y: a.y + ((c.y - a.y) * j) / k });
      });
      for (const x of xs)
        for (const y of ys) {
          const p = { x, y };
          if (!pointInOrOnPolygon(p, centre, 0.05)) continue;
          const toFacade = distanceToRing(p, centre);
          if (toFacade > 0.05 && toFacade < 1) continue;
          add(p);
        }
      levels.push(pts);
    }

    // Floor area each column carries: half the way to its neighbours on either side.
    const share = (p: Vec2, pts: readonly Vec2[]): number => {
      const reach = (axis: 'x' | 'y', sign: 1 | -1) => {
        const other = axis === 'x' ? 'y' : 'x';
        const band = (axis === 'x' ? gy : gx) * 0.6;
        const ahead = pts
          .filter((q) => Math.abs(q[other] - p[other]) < band && sign * (q[axis] - p[axis]) > 0.1)
          .map((q) => sign * (q[axis] - p[axis]));
        return ahead.length > 0 ? Math.min(...ahead) / 2 : 0;
      };
      return (reach('x', 1) + reach('x', -1)) * (reach('y', 1) + reach('y', -1));
    };

    for (let level = 0; level < n; level++) {
      const pts = levels[level] ?? [];
      const design = (p: Vec2) => {
        const area = share(p, pts);
        let ed = 0;
        let ek = 0;
        for (let k = level + 1; k <= n; k++) {
          const { g, q } = slabLoad(k);
          ed += area * (GAMMA_G * g + GAMMA_Q * q);
          ek += area * (g + q);
        }
        return { ed, ek };
      };
      const loads = pts.map(design);
      const sized = Math.max(
        MIN_COLUMN,
        ...loads.map((l) => roundUp(Math.sqrt((l.ed * SLENDERNESS) / COLUMN_STRESS), 0.05)),
      );
      const size = cfg?.columnSize ?? sized;
      columnSizes.push(size);
      const base = e[level] ?? 0;
      const top = (e[level + 1] ?? base) - d - drop;
      const outer = ensureCounterClockwise(
        storeyCorners(b, level).map((p) => worldToLocal(p, placement)),
      );
      const centre = insetPolygon(outer, tw / 2);
      const inside = (p: Vec2, hx: number, hy: number) => pushInside(p, hx, hy, outer);
      const placed = pts.map((p) => inside(p, size / 2, size / 2));
      pts.forEach((p, i) => {
        const edge = distanceToRing(p, centre) < 0.05;
        const at = placed[i] ?? p;
        const center = toWorld(at.x, at.y);
        columns.push({
          center,
          size,
          level,
          base,
          top,
          outline: square(center, size, b.rotation),
          obb: { center, width: size, depth: size, rotation: b.rotation },
          load: loads[i]?.ek ?? 0,
          edge,
        });
      });

      if (beam) {
        const width = beam.width;
        const lines = beamsAlongX ? ys : xs;
        const along = beamsAlongX ? 'x' : 'y';
        const across = beamsAlongX ? 'y' : 'x';
        const limit = 1.6 * Math.max(gx, gy);
        const half = beamsAlongX ? { x: 0, y: width / 2 } : { x: width / 2, y: 0 };
        for (const line of lines) {
          // Between the columns where they stand, an edge beam behind the facade like them.
          const on = pts
            .map((p, i) => ({ p, at: placed[i] ?? p }))
            .filter(({ p }) => Math.abs(p[across] - line) < 0.05)
            .sort((u, w) => u.p[along] - w.p[along]);
          on.slice(1).forEach((qq, j) => {
            const pp = on[j];
            if (!pp || qq.p[along] - pp.p[along] > limit) return;
            const p = inside(pp.at, half.x, half.y);
            const q = inside(qq.at, half.x, half.y);
            const a = toWorld(p.x, p.y);
            const c = toWorld(q.x, q.y);
            const outline = [
              toWorld(p.x - half.x, p.y - half.y),
              toWorld(q.x - half.x, q.y - half.y),
              toWorld(q.x + half.x, q.y + half.y),
              toWorld(p.x + half.x, p.y + half.y),
            ];
            beams.push({ a, b: c, level, top: top + drop, width, drop, outline });
          });
        }
      }
    }

    // A column standing on the slab of the storey below needs a column there or a transfer beam.
    for (let level = 1; level < n; level++) {
      const below = columns.filter((c) => c.level === level - 1);
      const loose = columns.filter(
        (c) =>
          c.level === level &&
          !below.some((x) => Math.hypot(x.center.x - c.center.x, x.center.y - c.center.y) < 0.3),
      );
      if (loose.length > 0)
        hints.push(fillText(t.unsupported, { name, n: loose.length, level: levelLabel(level, b) }));
    }

    // Names on the north and west side, like the axes of a hall.
    axes = [
      ...xs.map((x, i) => ({
        label: String(i + 1),
        a: toWorld(x, box.y0),
        b: toWorld(x, box.y1),
      })),
      ...[...ys].reverse().map((y, i) => ({
        label: String.fromCharCode(65 + i),
        a: toWorld(box.x1, y),
        b: toWorld(box.x0, y),
      })),
    ];
  }

  if (officeSlabLevels(o).length > 0 && o.slabThickness + 0.005 < slabRecommended)
    hints.push(
      fillText(t.slabThin, {
        name,
        thickness: cm(o.slabThickness),
        span: m1(span),
        recommended: cm(slabRecommended),
      }),
    );
  hints.push(...hallHints(doc, b, e[n] ?? b.eaveHeight));

  return {
    system,
    grid,
    fields: { nx: xs.length - 1, ny: ys.length - 1, x: gx, y: gy },
    axes,
    columns,
    beams,
    columnSizes,
    beam,
    span,
    slabRecommended,
    hints,
  };
}

/**
 * Span of slabs resting on walls: the widest gap across the building between its long
 * exterior walls and the massive inner walls running along them.
 */
function wallSpan(
  doc: StructureDoc,
  b: Building,
  box: { x0: number; x1: number; y0: number; y1: number },
  placement: { position: Vec2; rotation: number },
): number {
  const o = b.office;
  if (!o) return 0;
  const alongX = box.x1 - box.x0 >= box.y1 - box.y0;
  const length = alongX ? box.x1 - box.x0 : box.y1 - box.y0;
  const tw = b.wallThickness;
  const lo = (alongX ? box.y0 : box.x0) + tw / 2;
  const hi = (alongX ? box.y1 : box.x1) - tw / 2;
  const outline = buildingCorners(b);
  let widest = 0;
  // Each slab rests on the walls of the storey below it.
  for (const slab of officeSlabLevels(o)) {
    const level = slab - 1;
    const lines = [lo, hi];
    for (const w of Object.values(doc.walls)) {
      if (w.level !== level || w.kind !== 'interior' || !MASSIVE.includes(w.cladding)) continue;
      const mid = { x: (w.start.x + w.end.x) / 2, y: (w.start.y + w.end.y) / 2 };
      if (!pointInOrOnPolygon(mid, outline, 0.05)) continue;
      const a = worldToLocal(w.start, placement);
      const c = worldToLocal(w.end, placement);
      const run = alongX ? Math.abs(c.x - a.x) : Math.abs(c.y - a.y);
      const off = alongX ? Math.abs(c.y - a.y) : Math.abs(c.x - a.x);
      if (run < 0.5 * length || off > 0.2 * run) continue;
      lines.push(alongX ? (a.y + c.y) / 2 : (a.x + c.x) / 2);
    }
    lines.sort((p, q) => p - q);
    for (let i = 1; i < lines.length; i++)
      widest = Math.max(widest, (lines[i] ?? 0) - (lines[i - 1] ?? 0));
  }
  return widest;
}

/** An office in a hall: hall columns standing in it, and its top against the steel above. */
function hallHints(doc: StructureDoc, b: Building, officeTop: number): string[] {
  const o = b.office;
  if (!o || o.placement !== 'indoor') return [];
  const host = hostHall(doc, b);
  const frame = host ? projectHallFrame(doc, host) : null;
  if (!frame) return [];
  const t = modelTexts().officeStructure;
  const out: string[] = [];
  const floor = storeyCorners(b, 0);
  for (const c of frame.columns) {
    if (!pointInOrOnPolygon(c.center, floor, 0.05)) continue;
    const text =
      c.kind === 'gableColumn'
        ? fillText(t.hallGableColumn, { name: b.name })
        : fillText(t.hallColumn, { name: b.name, axis: c.axis ?? '' });
    if (!out.includes(text)) out.push(text);
  }
  const top = officeTop + (o.usableTop ? RAILING_HEIGHT : 0);
  const outline = buildingCorners(b);
  let lowest = Infinity;
  for (const m of frame.members) {
    if (m.kind !== 'rafter' && m.kind !== 'beam' && m.kind !== 'purlin' && m.kind !== 'strut')
      continue;
    const half = sectionOf(m.profile).h / 2;
    for (const k of [0, 0.25, 0.5, 0.75, 1]) {
      const p = { x: m.a.x + (m.b.x - m.a.x) * k, y: m.a.y + (m.b.y - m.a.y) * k };
      if (pointInOrOnPolygon(p, outline, 0))
        lowest = Math.min(lowest, m.a.z + (m.b.z - m.a.z) * k - half);
    }
  }
  if (Number.isFinite(lowest) && top > lowest)
    out.push(
      fillText(t.headroom, {
        name: b.name,
        top: formatNumber(top, 2, 2),
        steel: formatNumber(lowest, 2, 2),
      }),
    );
  return out;
}

/** Planning hints of all offices' structures, for the report. */
export const officeStructureHints = (doc: StructureDoc): string[] =>
  Object.values(doc.buildings).flatMap((b) => officeStructure(doc, b)?.hints ?? []);
